import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { testDocAPI } from '../../services/api';
import { DefectRow, DEFECT_STATUS, DEFECT_SEVERITY, SiteTag } from './common';
import { gray, font } from '../../theme/tokens';

/* 결함관리대장 탭
   실패 케이스는 결과 적재 시 **자동으로 대장에 올라온다**. 같은 키(site:screen:check)면
   재발로 격상하고 발생 횟수를 센다. 통과했다고 자동으로 닫지는 않는다 — 닫는 판단은 사람이 한다.
   노이즈는 '기각' 으로 닫으면 다시 자동 등록되지 않는다. */

interface Props { bodyHeight: number; canWrite: boolean; canDelete: boolean; }

const STATUS_LIST = ['신규', '조치중', '조치완료', '재발', '보류', '기각'];
const SEVERITY_LIST = ['치명', '중대', '보통', '경미'];

const DefectTab: React.FC<Props> = ({ bodyHeight, canWrite, canDelete }) => {
  const [rows, setRows] = useState<DefectRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  const [severity, setSeverity] = useState<string | undefined>();
  const [openOnly, setOpenOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<DefectRow | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, sm] = await Promise.all([
        testDocAPI.defects({ status, severity, open_only: openOnly, search: search || undefined,
                             page, page_size: pageSize }),
        testDocAPI.defectSummary(),
      ]);
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
      setSummary(sm.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '결함을 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, [status, severity, openOnly, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const doSave = async () => {
    try {
      const v = await form.validateFields();
      await testDocAPI.updateDefect(edit!.id, v);
      message.success('결함이 수정되었습니다.');
      setEdit(null);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '수정에 실패했습니다.');
    }
  };

  const columns = [
    { title: '결함번호', dataIndex: 'defect_no', width: 118,
      render: (v: string, r: DefectRow) => (
        <Space size={4}>
          <span style={{ fontFamily: font.mono, fontSize: 11.5, fontWeight: 600 }}>{v}</span>
          {r.source === 'auto' && <Tooltip title="실행 결과에서 자동 등록"><Tag style={{ margin: 0, fontSize: 10 }}>자동</Tag></Tooltip>}
        </Space>
      ) },
    { title: '사이트', dataIndex: 'site', width: 78, render: (v: string) => <SiteTag site={v} /> },
    { title: '대상', width: 265,
      render: (_: any, r: DefectRow) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.screen_name || r.screen_code}</div>
          <div style={{ fontSize: 11.5, color: gray[500] }}>{r.check_name || r.check_code}</div>
        </div>
      ) },
    { title: '심각도', dataIndex: 'severity', width: 80,
      render: (v: string) => (
        <Tag style={{ margin: 0, color: DEFECT_SEVERITY[v], borderColor: DEFECT_SEVERITY[v], fontWeight: 700 }}>{v}</Tag>
      ) },
    { title: '상태', dataIndex: 'status', width: 90,
      render: (v: string) => {
        const m = DEFECT_STATUS[v] || DEFECT_STATUS['신규'];
        return <Tag style={{ margin: 0, color: m.color, background: m.bg, borderColor: m.bg, fontWeight: 700 }}>{v}</Tag>;
      } },
    { title: '발생', dataIndex: 'occurrence_count', width: 72,
      render: (v: number) => <span style={{ fontWeight: 700, color: v > 1 ? '#b91c1c' : gray[600] }}>{v}회</span> },
    { title: '최근 실패', dataIndex: 'last_seen_at', width: 128,
      render: (v: string) => <span style={{ fontSize: 11.5, color: gray[500] }}>{v || '—'}</span> },
    { title: '최근 통과', dataIndex: 'last_passed_at', width: 128,
      render: (v: string, r: DefectRow) => {
        if (!v) return <span style={{ color: gray[300] }}>—</span>;
        const closable = !r.last_seen_at || v >= r.last_seen_at;
        return (
          <Tooltip title={closable ? '최근 실행에서는 통과 — 조치완료로 닫을 후보입니다' : ''}>
            <span style={{ fontSize: 11.5, color: closable ? '#047857' : gray[500], fontWeight: closable ? 600 : 400 }}>{v}</span>
          </Tooltip>
        );
      } },
    { title: '내용', dataIndex: 'last_detail', ellipsis: true,
      render: (v: string, r: DefectRow) => (
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{v || r.detail}</span>}>
          <span style={{ fontSize: 12, color: gray[600] }}>{((v || r.detail) || '').split('\n')[0]}</span>
        </Tooltip>
      ) },
    { title: '', width: 108, fixed: 'right' as const,
      render: (_: any, r: DefectRow) => (
        <Space size={4}>
          {canWrite && <Button size="small" onClick={() => {
            setEdit(r);
            form.setFieldsValue({
              status: r.status, severity: r.severity, title: r.title,
              cause: r.cause, action: r.action, fix_commit: r.fix_commit, assignee: r.assignee,
            });
          }}>조치</Button>}
          {canDelete && (
            <Popconfirm title="이 결함을 삭제할까요?" okText="삭제" cancelText="취소"
              onConfirm={async () => {
                try { await testDocAPI.removeDefect(r.id); message.success('삭제되었습니다.'); load(); }
                catch { message.error('삭제에 실패했습니다.'); }
              }}>
              <Button size="small" danger type="text">삭제</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select allowClear placeholder="상태" style={{ width: 120 }} value={status}
          onChange={v => { setStatus(v); setPage(1); }}
          options={STATUS_LIST.map(v => ({ value: v, label: v }))} />
        <Select allowClear placeholder="심각도" style={{ width: 110 }} value={severity}
          onChange={v => { setSeverity(v); setPage(1); }}
          options={SEVERITY_LIST.map(v => ({ value: v, label: v }))} />
        <Button size="middle" type={openOnly ? 'primary' : 'default'}
          onClick={() => { setOpenOnly(!openOnly); setPage(1); }}>미해결만</Button>
        <Input.Search allowClear placeholder="제목·화면·내용 검색" style={{ width: 230 }}
          onSearch={v => { setSearch(v); setPage(1); }}
          onChange={e => { if (!e.target.value) { setSearch(''); setPage(1); } }} />
        <Button icon={<ReloadOutlined />} onClick={load} />
        {summary && (
          <Space size={6} style={{ marginLeft: 'auto' }} wrap>
            <Tag style={{ margin: 0, fontWeight: 700 }}>미해결 {summary.open}</Tag>
            {summary.reopened > 0 && <Tag color="warning" style={{ margin: 0, fontWeight: 700 }}>재발 {summary.reopened}</Tag>}
            {summary.closable > 0 && (
              <Tooltip title="최근 실행에서는 통과 — 조치완료로 닫을 후보">
                <Tag color="success" style={{ margin: 0, fontWeight: 700 }}>닫기 후보 {summary.closable}</Tag>
              </Tooltip>
            )}
            <span style={{ fontSize: 12, color: gray[400] }}>전체 {summary.total}건</span>
          </Space>
        )}
      </div>

      <Table<DefectRow>
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns as any}
        scroll={{ y: bodyHeight, x: 1400 }}
        rowClassName={(_, i) => (i % 2 ? 'table-row-alt' : '')}
        pagination={{
          current: page, pageSize, total, showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
          onChange: (p, ps) => { setPage(ps !== pageSize ? 1 : p); setPageSize(ps); },
          size: 'small',
        }}
      />

      <Modal open={!!edit} onCancel={() => setEdit(null)} onOk={doSave} okText="저장" cancelText="취소"
        width={680} title={edit && (
          <Space>
            <span style={{ fontFamily: font.mono, fontSize: 13 }}>{edit.defect_no}</span>
            <span style={{ fontWeight: 400, fontSize: 13, color: gray[500] }}>
              [{edit.site}] {edit.screen_name} · {edit.check_name} · {edit.occurrence_count}회 발생
            </span>
          </Space>
        )}>
        {edit && (
          <Form form={form} layout="vertical" size="small" style={{ marginTop: 8 }}>
            <div style={{ background: '#f8fafc', border: `1px solid ${gray[200]}`, borderRadius: 8,
                          padding: '8px 12px', marginBottom: 12, fontSize: 12, whiteSpace: 'pre-wrap',
                          maxHeight: 140, overflow: 'auto' }}>
              {edit.last_detail || edit.detail || '내용 없음'}
            </div>
            <Space size={12}>
              <Form.Item name="status" label="상태" style={{ width: 140 }}>
                <Select options={STATUS_LIST.map(v => ({ value: v, label: v }))} />
              </Form.Item>
              <Form.Item name="severity" label="심각도" style={{ width: 130 }}>
                <Select options={SEVERITY_LIST.map(v => ({ value: v, label: v }))} />
              </Form.Item>
              <Form.Item name="assignee" label="담당자" style={{ width: 150 }}><Input /></Form.Item>
              <Form.Item name="fix_commit" label="조치 커밋" style={{ width: 170 }}><Input placeholder="b79d230" /></Form.Item>
            </Space>
            <Form.Item name="title" label="제목"><Input /></Form.Item>
            <Form.Item name="cause" label="원인"><Input.TextArea rows={3} /></Form.Item>
            <Form.Item name="action" label="조치 내용"><Input.TextArea rows={3} /></Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default DefectTab;
